import { useEffect, useId, useRef } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import clsx from 'clsx';
import { Icon } from './Icon';
import { IconButton } from './IconButton';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Secondary header line, e.g. the document path of the section being read. */
  subtitle?: string | undefined;
  children?: ReactNode;
  /**
   * Width constraint for the panel. The Figma reference modal (node
   * 1061:112780) is 726px wide — the default.
   */
  widthClassName?: string | undefined;
};

/** Tab-order candidates inside the panel — disabled and tabindex=-1 excluded. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * General-purpose modal primitive (F-016 T040) — the house dialog. Renders
 * nothing while closed; while open it is a full-viewport scrim with a centred
 * panel styled to the approved Figma reference (node 1061:112780): white
 * rounded-8 container, 16px-medium title + 12px subtitle header, ✕ top-right,
 * and the content area below a 1px #f2f3f6 hairline.
 *
 * Behaviour: ESC, backdrop click and the ✕ all call `onClose`; focus moves
 * into the panel on open, Tab/Shift+Tab cycle within it (focus trap), and
 * focus is restored to the previously focused element on close.
 *
 * (`VerificationProcessingModal` predates this and stays single-purpose.)
 */
export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  widthClassName = 'max-w-[726px]',
}: DialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // The key handler lives on `document` for the whole open period; reading
  // `onClose` through a ref keeps the effect from re-running (and stealing
  // focus back to the panel) when a parent re-creates the callback.
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;

      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (!first || !last) {
        // No focusable content — keep focus pinned to the panel itself.
        event.preventDefault();
        panel.focus();
        return;
      }

      const active = document.activeElement;
      const withinTrap = active instanceof HTMLElement && focusable.includes(active);
      if (event.shiftKey) {
        // Backward from the first control (or from the panel itself) wraps to the last.
        if (!withinTrap || active === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (!withinTrap || active === last) {
        // Forward from the last control (or from the panel itself) wraps to the first.
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      data-dialog-backdrop
      // Full-viewport scrim — rgba(0,0,0,0.6) per the Figma reference.
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={clsx(
          'flex max-h-[90vh] w-full flex-col overflow-hidden rounded-md bg-surface-card shadow-lg outline-none',
          widthClassName,
        )}
      >
        <div data-dialog-header className="flex items-start justify-between gap-4 px-4 pb-3 pt-4">
          <div className="flex min-w-0 flex-col gap-[2px]">
            {/* Figma 1061:112780 header — 16px medium title, 12px regular subtitle, #111. */}
            <h2 id={titleId} className="m-0 truncate text-xl font-medium leading-snug text-[#111]">
              {title}
            </h2>
            {subtitle && (
              <p data-dialog-subtitle className="m-0 truncate text-sm font-normal text-[#111]">
                {subtitle}
              </p>
            )}
          </div>
          <IconButton
            size="sm"
            aria-label="Close"
            onClick={onClose}
            className="-mr-1 -mt-1 flex-shrink-0"
          >
            <Icon name="close" size={16} />
          </IconButton>
        </div>
        {/* Content area sits below a 1px hairline (#f2f3f6 = the surface-workspace token). */}
        <div data-dialog-body className="min-h-0 border-t border-surface-workspace p-4">
          {children}
        </div>
      </div>
    </div>
  );
}
